import { ref, watch, computed } from 'vue'
import { safeLocalStorage, isClient } from '@/composables/useSSR'

const STORAGE_KEY = 'userNavCollapseState'

// Group keys for user navigation
export type UserNavGroupKey = 'shop' | 'services' | 'finance' | 'account'

interface CollapseState {
  shop: boolean
  services: boolean
  finance: boolean
  account: boolean
}

// Default: Services and Finance expanded, Shop and Account collapsed
const defaultState: CollapseState = {
  shop: true,
  services: false,
  finance: false,
  account: true
}

// Load from localStorage or use defaults
const loadState = (): CollapseState => {
  const storage = safeLocalStorage()
  if (!storage) return defaultState

  const stored = storage.getItem(STORAGE_KEY)
  if (stored) {
    try {
      return { ...defaultState, ...JSON.parse(stored) }
    } catch {
      return defaultState
    }
  }
  return defaultState
}

// Shared reactive state
const collapseState = ref<CollapseState>(isClient ? loadState() : defaultState)

// Sync to localStorage (client only)
if (isClient) {
  watch(
    collapseState,
    (newValue) => {
      const storage = safeLocalStorage()
      if (storage) {
        storage.setItem(STORAGE_KEY, JSON.stringify(newValue))
      }
    },
    { deep: true }
  )
}

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      try {
        collapseState.value = { ...defaultState, ...JSON.parse(event.newValue) }
      } catch {
        // Ignore parse errors
      }
    }
  })
}

export function useUserNavCollapse() {
  const isCollapsed = (groupKey: UserNavGroupKey): boolean => {
    return collapseState.value[groupKey]
  }

  const toggle = (groupKey: UserNavGroupKey) => {
    collapseState.value = {
      ...collapseState.value,
      [groupKey]: !collapseState.value[groupKey]
    }
  }

  const expand = (groupKey: UserNavGroupKey) => {
    if (collapseState.value[groupKey]) {
      collapseState.value = {
        ...collapseState.value,
        [groupKey]: false
      }
    }
  }

  const expandAll = () => {
    collapseState.value = { shop: false, services: false, finance: false, account: false }
  }

  const collapseAll = () => {
    collapseState.value = { shop: true, services: true, finance: true, account: true }
  }

  return {
    collapseState: computed(() => collapseState.value),
    isCollapsed,
    toggle,
    expand,
    expandAll,
    collapseAll
  }
}
